import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Users, Plus, Loader2, Search, Pencil, Trash2, Key, Shield,
  Building2, MapPin, Eye, EyeOff, UserCheck, UserX,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function UserManagementV2({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', role: 'viewer', tenant_id: '', property_ids: [] });
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', tenant_id: '', property_ids: [], active: true, permissions: [] });
  const [newPw, setNewPw] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [u, t, r] = await Promise.all([
        axios.get(`${BACKEND}/api/users`, { headers }),
        axios.get(`${BACKEND}/api/tenants`, { headers }).catch(() => ({ data: { tenants: [] } })),
        axios.get(`${BACKEND}/api/roles`, { headers }).catch(() => ({ data: { roles: [] } })),
      ]);
      setUsers(u.data.users || []);
      setTenants(t.data.tenants || []);
      setRoles(r.data.roles || []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch properties when tenant changes in create/edit
  const fetchPropertiesForTenant = async (tid) => {
    try {
      const res = await axios.get(`${BACKEND}/api/properties`, { headers });
      setProperties(res.data || []);
    } catch { setProperties([]); }
  };

  const openCreate = () => {
    setCreateForm({ email: '', name: '', password: '', role: 'viewer', tenant_id: '', property_ids: [] });
    fetchPropertiesForTenant('');
    setCreateOpen(true);
  };

  const openEdit = async (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '', email: user.email || '', role: user.role || 'viewer',
      tenant_id: user.tenant_id || '', property_ids: user.property_ids || [],
      active: user.active !== false, permissions: user.permissions || [],
    });
    fetchPropertiesForTenant(user.tenant_id || '');
    setEditOpen(true);
  };

  const createUser = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND}/api/users`, createForm, { headers });
      toast.success('User created');
      setCreateOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const updateUser = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/users/${selectedUser.id}`, editForm, { headers });
      toast.success('User updated');
      setEditOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await axios.delete(`${BACKEND}/api/users/${uid}`, { headers });
      toast.success('User deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resetPassword = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND}/api/users/${selectedUser.id}/reset-password`, { password: newPw }, { headers });
      toast.success('Password reset');
      setResetPwOpen(false);
      setNewPw('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q));
  }, [users, search]);

  const ROLE_COLORS = { super_admin: 'bg-red-100 text-red-700', admin: 'bg-blue-100 text-blue-700', property_manager: 'bg-emerald-100 text-emerald-700', viewer: 'bg-slate-100 text-slate-600' };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const UserFormFields = ({ form, setForm, isCreate }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Jane Smith" className="mt-1" /></div>
        <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@company.com" className="mt-1" /></div>
      </div>
      {isCreate && (
        <div><Label className="text-xs">Password</Label>
          <div className="relative mt-1"><Input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Minimum 6 characters" /><button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPw(!showPw)}>{showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button></div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Role</Label>
          <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Tenant</Label>
          <Select value={form.tenant_id || '__none__'} onValueChange={v => { const tid = v === '__none__' ? '' : v; setForm(p => ({ ...p, tenant_id: tid })); fetchPropertiesForTenant(tid); }}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Super Admin (no tenant)</SelectItem>
              {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {form.tenant_id && properties.length > 0 && (
        <div>
          <Label className="text-xs">Property Access ({(form.property_ids || []).length} selected — empty = all properties)</Label>
          <ScrollArea className="mt-1 max-h-[150px] border rounded-lg p-2">
            <div className="space-y-1">
              {properties.filter(p => p.id !== 'corporate').map(p => (
                <label key={p.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/40 cursor-pointer text-sm">
                  <Checkbox checked={(form.property_ids || []).includes(p.id)} onCheckedChange={() => setForm(prev => ({ ...prev, property_ids: prev.property_ids?.includes(p.id) ? prev.property_ids.filter(x => x !== p.id) : [...(prev.property_ids || []), p.id] }))} />
                  {p.name}
                </label>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
      {!isCreate && (
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div><span className="text-sm font-medium">Active Account</span><p className="text-[10px] text-muted-foreground">Disabled accounts cannot log in</p></div>
          <Switch checked={form.active !== false} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} />
        </div>
      )}
      {/* Permissions */}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Permissions</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-1.5 p-3 rounded-lg border bg-muted/10">
          {[
            { id: 'edit_products', label: 'Edit Products', group: 'Products' },
            { id: 'add_delete_products', label: 'Add/Delete Products', group: 'Products' },
            { id: 'edit_vendors', label: 'Edit Vendors', group: 'Vendors' },
            { id: 'add_delete_vendors', label: 'Add/Delete Vendors', group: 'Vendors' },
            { id: 'edit_platforms', label: 'Edit Platforms', group: 'Platforms' },
            { id: 'add_delete_platforms', label: 'Add/Delete Platforms', group: 'Platforms' },
            { id: 'edit_assignments', label: 'Edit Assignments', group: 'Operations' },
            { id: 'edit_tenant_statuses', label: 'Edit Review Statuses', group: 'Operations' },
            { id: 'manage_users', label: 'Manage Users', group: 'Admin' },
            { id: 'view_financials', label: 'View Financials', group: 'Admin' },
          ].map(perm => (
            <label key={perm.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/40 cursor-pointer text-xs">
              <Checkbox
                checked={(form.permissions || []).includes(perm.id)}
                onCheckedChange={() => setForm(prev => ({
                  ...prev,
                  permissions: (prev.permissions || []).includes(perm.id)
                    ? prev.permissions.filter(p => p !== perm.id)
                    : [...(prev.permissions || []), perm.id]
                }))}
              />
              <span>{perm.label}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 mt-1.5">
          <button type="button" className="text-[10px] text-primary hover:underline" onClick={() => setForm(p => ({
            ...p, permissions: ['edit_products', 'add_delete_products', 'edit_vendors', 'add_delete_vendors', 'edit_assignments', 'edit_tenant_statuses', 'manage_users', 'view_financials']
          }))}>Tenant Admin Defaults</button>
          <button type="button" className="text-[10px] text-primary hover:underline" onClick={() => setForm(p => ({ ...p, permissions: [] }))}>Clear All</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="user-management-v2">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>User Management</h2>
          <p className="text-sm text-muted-foreground">{users.length} users across {tenants.length} tenants</p>
        </div>
        <div className="flex gap-2">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
          </div>
          <Button variant="outline" size="sm" onClick={openCreate} className="gap-1 text-xs" data-testid="create-user-btn"><Plus className="h-3 w-3" /> New User</Button>
        </div>
      </div>

      {/* User table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50">
            <th className="p-2.5 text-left text-xs font-semibold">User</th>
            <th className="p-2.5 text-left text-xs font-semibold">Role</th>
            <th className="p-2.5 text-left text-xs font-semibold">Tenant</th>
            <th className="p-2.5 text-center text-xs font-semibold">Properties</th>
            <th className="p-2.5 text-center text-xs font-semibold">Perms</th>
            <th className="p-2.5 text-center text-xs font-semibold">Status</th>
            <th className="p-2.5 text-center text-xs font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-t hover:bg-muted/20">
                <td className="p-2.5">
                  <div className="font-medium">{u.name || 'Unnamed'}</div>
                  <div className="text-[10px] text-muted-foreground">{u.email}</div>
                </td>
                <td className="p-2.5"><Badge className={`text-[9px] ${ROLE_COLORS[u.role] || 'bg-slate-100'}`}>{u.role_name || u.role}</Badge></td>
                <td className="p-2.5 text-xs">{u.tenant_name || '-'}</td>
                <td className="p-2.5 text-center text-xs font-mono">{u.property_count || 'All'}</td>
                <td className="p-2.5 text-center">
                  <Badge variant="outline" className="text-[9px] font-mono">{(u.permissions || []).length}</Badge>
                </td>
                <td className="p-2.5 text-center">{u.active !== false ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Active</Badge> : <Badge className="text-[9px] bg-red-100 text-red-700">Disabled</Badge>}</td>
                <td className="p-2.5 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(u)} title="Edit"><Pencil className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelectedUser(u); setResetPwOpen(true); setNewPw(''); }} title="Reset Password"><Key className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteUser(u.id)} title="Delete"><Trash2 className="h-3 w-3 text-destructive" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No users found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
          <UserFormFields form={createForm} setForm={setCreateForm} isCreate />
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={createUser} disabled={saving || !createForm.email || !createForm.name || !createForm.password}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Create User</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Edit User — {selectedUser?.name}</DialogTitle></DialogHeader>
          <UserFormFields form={editForm} setForm={setEditForm} isCreate={false} />
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button><Button onClick={updateUser} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwOpen} onOpenChange={setResetPwOpen}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Set a new password for {selectedUser?.name} ({selectedUser?.email})</p>
          <div className="relative"><Input type={showPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password (6+ chars)" /><button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPw(!showPw)}>{showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button></div>
          <DialogFooter><Button variant="outline" onClick={() => setResetPwOpen(false)}>Cancel</Button><Button onClick={resetPassword} disabled={saving || newPw.length < 6}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Reset Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
